import { useId } from 'react';
import { X, Trophy, TrendingUp, Trash2, Edit2 } from 'lucide-react';
import { getEloLevel } from '../../engine';
import { useT } from '../../i18n';
import { PlayerLinkInline } from '../players';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

export interface MatchDetailModalProps {
  /** The single match to display (teams, sets, result with ELO breakdown). */
  match: any;
  /** Resolves a player id → display name (kept for parity with the call-site). */
  getName: (id: string) => string;
  /** Map of player id → player object, used to render names/levels/ELO. */
  playerById: Record<string, any>;
  /** Closes the modal. */
  onClose: () => void;
  /** Deletes this match. */
  onDelete: () => void;
  /** Opens the edit flow for this match. */
  onEdit: () => void;
  /** Optional: navigate to a player's profile (called after onClose). */
  onPlayerClick?: (p: any) => void;
  /**
   * Live engine wiring object. Needed for
   * Padel24Engine.elo.K_FACTOR and Padel24Engine.elo.formatBoost in the
   * ELO-breakdown section. Passed as a prop because the object itself is used.
   */
  Padel24Engine: any;
}

/**
 * Single-match detail modal (teams, set scores, ELO breakdown, MOV); extracted
 * verbatim from the original monolith (legacy/App.jsx).
 *
 * Deviation from the original: `getName` is part of the props contract (the
 * call-site still passes it) but the component body never references it, so it
 * is intentionally not destructured here — the original took it as an unused
 * binding, harmless under @ts-nocheck, but our `--max-warnings=0` lint rejects
 * an unused arg. Behaviour is unchanged.
 */
export function MatchDetailModal({
  match,
  playerById,
  onClose,
  onDelete,
  onEdit,
  onPlayerClick,
  Padel24Engine,
}: MatchDetailModalProps) {
  const t = useT();
  const uid = safeId('single-match-detail', useId());
  const ids = { title: `${uid}-title`, sub: `${uid}-sub` };
  const date = new Date(match.playedAt);
  const t1Won = match.result?.winner === 'team1';

  // Fetch players
  const t1p1 = playerById[match.team1.p1Id];
  const t1p2 = playerById[match.team1.p2Id];
  const t2p1 = playerById[match.team2.p1Id];
  const t2p2 = playerById[match.team2.p2Id];

  // Stats per set and total
  const setsData = (match.sets || []).map((s, i) => {
    const t1Won = s.games1 > s.games2;
    const total = s.games1 + s.games2;
    return {
      setNumber: i + 1,
      games1: s.games1,
      games2: s.games2,
      t1Won,
      total,
      diff: Math.abs(s.games1 - s.games2),
    };
  });
  const t1SetWins = setsData.filter((s) => s.t1Won).length;
  const t2SetWins = setsData.filter((s) => !s.t1Won).length;
  const t1TotalGames = setsData.reduce((sum, s) => sum + s.games1, 0);
  const t2TotalGames = setsData.reduce((sum, s) => sum + s.games2, 0);
  const totalGames = t1TotalGames + t2TotalGames;
  const winnerGamesPercent = t1Won
    ? totalGames > 0
      ? (t1TotalGames / totalGames) * 100
      : 0
    : totalGames > 0
      ? (t2TotalGames / totalGames) * 100
      : 0;

  // Helper: player row with level + ELO change
  const PlayerDetailRow = ({ playerId }) => {
    const p = playerById[playerId];
    if (!p) return <div className="text-xs text-ink-3 italic">Ukjent spiller</div>;
    const preElo = match.result?.playerEloPre?.[playerId];
    const change =
      match.team1.p1Id === playerId || match.team1.p2Id === playerId
        ? match.result?.t1Change
        : match.result?.t2Change;
    const eloDisplay = preElo != null ? preElo : p.elo;
    const eloAfter = preElo != null && change != null ? preElo + change : null;
    const level = getEloLevel(eloDisplay);

    return (
      <div className="flex items-center gap-2 py-1.5">
        <span
          className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
          style={{ background: level.color, color: level.text }}
          title={`Nivå ${level.level} — ${level.name}`}
        >
          {level.level}
        </span>
        <button
          onClick={() => {
            onClose();
            onPlayerClick?.(p);
          }}
          className="text-sm font-semibold text-ink hover:underline truncate flex-1 min-w-0 text-left"
          style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          {p.name}
        </button>
        <div className="text-right flex-shrink-0">
          <div className="font-mono text-xs text-ink-2">
            {eloDisplay}
            {eloAfter != null && (
              <>
                <span className="text-ink-4 mx-1">→</span>
                <span className="font-bold">{eloAfter}</span>
              </>
            )}
          </div>
          {change != null && (
            <div
              className={`font-mono text-[10px] font-bold ${change > 0 ? 'text-lime-deep' : change < 0 ? 'text-rose' : 'text-ink-3'}`}
            >
              {change > 0 ? '+' : ''}
              {change}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Team-average info
  const t1AvgPre = match.result?.t1EloBefore != null ? Math.round(match.result.t1EloBefore) : null;
  const t2AvgPre = match.result?.t2EloBefore != null ? Math.round(match.result.t2EloBefore) : null;
  const eloDiff = t1AvgPre != null && t2AvgPre != null ? Math.abs(t1AvgPre - t2AvgPre) : null;
  const t1WasFavorite = t1AvgPre != null && t2AvgPre != null && t1AvgPre > t2AvgPre;
  const t2WasFavorite = t1AvgPre != null && t2AvgPre != null && t2AvgPre > t1AvgPre;
  const upset = (t1Won && t2WasFavorite) || (!t1Won && t1WasFavorite);

  // MOV — margin of victory
  const movMultiplier = match.result?.mov;

  return (
    // A `Dialog` (#900): named by its «X & Y vant 2–1» headline and described by
    // the date line, Tab kept inside, Escape closes as the backdrop does, focus
    // given back.
    <Dialog
      labelledBy={ids.title}
      describedBy={ids.sub}
      onClose={onClose}
      showClose={false}
      backdropClassName="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      className="card w-full max-w-2xl flex flex-col"
      style={{ maxHeight: '92vh' }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-line flex-shrink-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="font-mono text-[10px] font-bold px-2 py-0.5 rounded"
              style={{ background: '#60a5fa40', color: '#0f1611' }}
            >
              EK
            </span>
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
              Enkeltkamp
            </span>
            {upset && (
              <span className="pill bg-amber-100 text-amber-900 text-[10px] font-bold">
                ⚡ Overraskelse
              </span>
            )}
          </div>
          <h2 id={ids.title} className="font-display text-xl font-semibold leading-tight">
            {t1Won ? (
              <span>
                <PlayerLinkInline p={t1p1} onClose={onClose} onPlayerClick={onPlayerClick} /> &{' '}
                <PlayerLinkInline p={t1p2} onClose={onClose} onPlayerClick={onPlayerClick} />
              </span>
            ) : (
              <span>
                <PlayerLinkInline p={t2p1} onClose={onClose} onPlayerClick={onPlayerClick} /> &{' '}
                <PlayerLinkInline p={t2p2} onClose={onClose} onPlayerClick={onPlayerClick} />
              </span>
            )}
            <span className="text-ink-3 mx-2">vant</span>
            <span className="font-mono">
              {t1Won ? `${t1SetWins}–${t2SetWins}` : `${t2SetWins}–${t1SetWins}`}
            </span>
          </h2>
          <p id={ids.sub} className="text-xs text-ink-3 font-mono mt-1">
            {date.toLocaleDateString('no-NO', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
            {' · '}
            {date.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('ui.dialog.close')}
          className="p-1 text-ink-3 hover:text-ink-2 flex-shrink-0"
        >
          <X size={20} />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {/* Team 1 (Home) */}
        <div
          className={`rounded-lg p-3 mb-2 border ${t1Won ? 'bg-lime-bg/20 border-lime-deep/30' : 'bg-bg-soft border-line'}`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 font-semibold">
                Lag 1 · Hjemme
              </span>
              {t1WasFavorite && (
                <span
                  className="pill text-[9px] font-bold"
                  style={{ background: '#dcfce7', color: '#166534' }}
                >
                  Favoritt
                </span>
              )}
              {t1Won && <Trophy size={12} className="text-lime-deep" />}
            </div>
            {t1AvgPre != null && (
              <div className="text-right">
                <div className="font-mono text-[10px] text-ink-3">Snitt-ELO</div>
                <div className="font-mono text-sm font-bold">{t1AvgPre}</div>
              </div>
            )}
          </div>
          <div className="divide-y divide-line/50">
            <PlayerDetailRow playerId={match.team1.p1Id} />
            <PlayerDetailRow playerId={match.team1.p2Id} />
          </div>
        </div>

        {/* Set result BETWEEN the teams — always 3 slots (fixed layout) */}
        <div className="my-3 px-2">
          <div
            className="grid gap-2 items-center"
            style={{ gridTemplateColumns: '70px 1fr 1fr 1fr 70px' }}
          >
            {/* Top row: column labels */}
            <div></div>
            <div className="text-center font-mono text-[9px] text-ink-3 uppercase tracking-wider">
              Sett 1
            </div>
            <div className="text-center font-mono text-[9px] text-ink-3 uppercase tracking-wider">
              Sett 2
            </div>
            <div className="text-center font-mono text-[9px] text-ink-3 uppercase tracking-wider">
              Sett 3
            </div>
            <div></div>

            {/* Team 1 score row */}
            <div className="text-right font-mono text-[10px] font-semibold text-ink-2 uppercase tracking-wider">
              Lag 1
            </div>
            {[0, 1, 2].map((idx) => {
              const s = setsData[idx];
              if (!s)
                return (
                  <div
                    key={`t1-${idx}`}
                    className="text-center font-mono text-xl py-1.5 rounded bg-bg-soft/30 text-ink-3"
                  >
                    —
                  </div>
                );
              return (
                <div
                  key={`t1-${idx}`}
                  className={`text-center font-mono text-2xl font-bold py-1.5 rounded ${
                    s.t1Won ? 'bg-lime-bg/40 text-lime-deep' : 'bg-bg-soft text-ink-3'
                  }`}
                >
                  {s.games1}
                </div>
              );
            })}
            <div className="text-left font-mono text-sm font-bold text-ink-2">
              {t1Won && (
                <span className="text-lime-deep">
                  {t1SetWins}–{t2SetWins}
                </span>
              )}
              {!t1Won && (
                <span className="text-ink-3">
                  {t1SetWins}–{t2SetWins}
                </span>
              )}
            </div>

            {/* Team 2 score row */}
            <div className="text-right font-mono text-[10px] font-semibold text-ink-2 uppercase tracking-wider">
              Lag 2
            </div>
            {[0, 1, 2].map((idx) => {
              const s = setsData[idx];
              if (!s)
                return (
                  <div
                    key={`t2-${idx}`}
                    className="text-center font-mono text-xl py-1.5 rounded bg-bg-soft/30 text-ink-3"
                  >
                    —
                  </div>
                );
              return (
                <div
                  key={`t2-${idx}`}
                  className={`text-center font-mono text-2xl font-bold py-1.5 rounded ${
                    !s.t1Won ? 'bg-lime-bg/40 text-lime-deep' : 'bg-bg-soft text-ink-3'
                  }`}
                >
                  {s.games2}
                </div>
              );
            })}
            <div className="text-left font-mono text-sm font-bold text-ink-2">
              {!t1Won && (
                <span className="text-lime-deep">
                  {t2SetWins}–{t1SetWins}
                </span>
              )}
              {t1Won && (
                <span className="text-ink-3">
                  {t2SetWins}–{t1SetWins}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Team 2 (Away) */}
        <div
          className={`rounded-lg p-3 mb-4 border ${!t1Won ? 'bg-lime-bg/20 border-lime-deep/30' : 'bg-bg-soft border-line'}`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 font-semibold">
                Lag 2 · Borte
              </span>
              {t2WasFavorite && (
                <span
                  className="pill text-[9px] font-bold"
                  style={{ background: '#dcfce7', color: '#166534' }}
                >
                  Favoritt
                </span>
              )}
              {!t1Won && <Trophy size={12} className="text-lime-deep" />}
            </div>
            {t2AvgPre != null && (
              <div className="text-right">
                <div className="font-mono text-[10px] text-ink-3">Snitt-ELO</div>
                <div className="font-mono text-sm font-bold">{t2AvgPre}</div>
              </div>
            )}
          </div>
          <div className="divide-y divide-line/50">
            <PlayerDetailRow playerId={match.team2.p1Id} />
            <PlayerDetailRow playerId={match.team2.p2Id} />
          </div>
        </div>

        {/* Statistics row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <div className="rounded-lg bg-bg-soft p-3">
            <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3 mb-1">
              Totale games
            </div>
            <div className="font-mono text-sm font-bold">
              {t1TotalGames}–{t2TotalGames}
            </div>
          </div>
          <div className="rounded-lg bg-bg-soft p-3">
            <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3 mb-1">
              Vinner-dominans
            </div>
            <div className="font-mono text-sm font-bold">{Math.round(winnerGamesPercent)}%</div>
            <div className="font-mono text-[9px] text-ink-3">av games</div>
          </div>
          <div className="rounded-lg bg-bg-soft p-3">
            <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3 mb-1">
              ELO-forskjell
            </div>
            <div className="font-mono text-sm font-bold">{eloDiff != null ? eloDiff : '—'}</div>
            <div className="font-mono text-[9px] text-ink-3">før kampen</div>
          </div>
          <div className="rounded-lg bg-bg-soft p-3">
            <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3 mb-1">
              MOV-multiplier
            </div>
            <div className="font-mono text-sm font-bold">
              {movMultiplier != null ? movMultiplier.toFixed(2) : '—'}×
            </div>
            <div className="font-mono text-[9px] text-ink-3">
              {movMultiplier >= 1.5 ? 'Klar seier' : movMultiplier >= 1.2 ? 'Solid' : 'Knepent'}
            </div>
          </div>
        </div>

        {/* ELO calculation — shows how the number came to be, step by step.
              Gives Andreas insight into how the K-factor, MOV and new-player boost
              combine into the actual change. */}
        {match.result &&
          match.result.t1Change != null &&
          (() => {
            const K = match.kFactor != null ? match.kFactor : Padel24Engine.elo.K_FACTOR || 32;
            const mov = match.result.mov || 1;
            const t1NewbieMul = match.result.t1NewbieMul ?? 1.0;
            const t2NewbieMul = match.result.t2NewbieMul ?? 1.0;
            const t1Elo = match.result.t1EloBefore;
            const t2Elo = match.result.t2EloBefore;
            // Expected outcome based on ELO (same as the chess formula)
            const expected1 = 1 / (1 + Math.pow(10, (t2Elo - t1Elo) / 400));
            const expected2 = 1 - expected1;
            const actual1 = t1Won ? 1 : 0;
            const actual2 = 1 - actual1;
            // Base change (without new-player boost)
            const baseT1 = Math.round(K * mov * (actual1 - expected1));
            const baseT2 = Math.round(K * mov * (actual2 - expected2));

            return (
              <div className="rounded-lg border border-line bg-bg-soft/40 p-3 mb-4">
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 font-semibold mb-2 flex items-center gap-1.5">
                  <TrendingUp size={11} /> ELO-beregning
                </div>

                {/* Formula explanation */}
                <div className="text-[11px] text-ink-3 mb-3 font-mono leading-relaxed">
                  Endring = K-faktor × MOV × ny-spiller-boost × (faktisk − forventet)
                </div>

                {/* Per-team breakdown */}
                <div className="space-y-2">
                  {[
                    {
                      label: 'Lag 1 (hjemme)',
                      newbieMul: t1NewbieMul,
                      baseChange: baseT1,
                      actualChange: match.result.t1Change,
                      expected: expected1,
                      actual: actual1,
                    },
                    {
                      label: 'Lag 2 (borte)',
                      newbieMul: t2NewbieMul,
                      baseChange: baseT2,
                      actualChange: match.result.t2Change,
                      expected: expected2,
                      actual: actual2,
                    },
                  ].map((row, idx) => (
                    <div key={idx} className="bg-paper rounded border border-line/60 p-2">
                      <div className="font-mono text-[10px] font-semibold text-ink-2 mb-1.5">
                        {row.label}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-[10px] font-mono">
                        <div>
                          <div className="text-ink-3 uppercase tracking-wider text-[9px]">
                            Forventet
                          </div>
                          <div className="text-ink-2 font-semibold">
                            {(row.expected * 100).toFixed(1)}%
                          </div>
                        </div>
                        <div>
                          <div className="text-ink-3 uppercase tracking-wider text-[9px]">
                            Faktisk
                          </div>
                          <div className="text-ink-2 font-semibold">
                            {row.actual === 1 ? 'Vant' : 'Tapte'}
                          </div>
                        </div>
                        <div>
                          <div className="text-ink-3 uppercase tracking-wider text-[9px]">
                            Basis
                          </div>
                          <div className="text-ink-2 font-semibold">
                            {row.baseChange > 0 ? '+' : ''}
                            {row.baseChange}
                          </div>
                        </div>
                        <div>
                          <div className="text-ink-3 uppercase tracking-wider text-[9px]">
                            Ny-spiller
                          </div>
                          <div className="text-ink-2 font-semibold">
                            {Padel24Engine.elo.formatBoost(row.newbieMul)}
                          </div>
                        </div>
                        <div>
                          <div className="text-ink-3 uppercase tracking-wider text-[9px]">
                            Endring
                          </div>
                          <div
                            className={`font-bold ${
                              row.actualChange > 0
                                ? 'text-lime-deep'
                                : row.actualChange < 0
                                  ? 'text-rose'
                                  : 'text-ink-3'
                            }`}
                          >
                            {row.actualChange > 0 ? '+' : ''}
                            {row.actualChange}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <p className="text-[10px] text-ink-3 mt-2 italic">
                  K-faktor ({K}) styrer hvor mye hver kamp påvirker ELO. MOV ({mov.toFixed(2)}×) er
                  bonus for dominerende seier. Ny-spiller-boost gir nye spillere forhøyet utslag som
                  avtar etter hvert som de spiller.
                </p>
              </div>
            );
          })()}

        {/* Format info */}
        <div className="rounded-lg border border-line p-3 text-[11px] text-ink-3 font-mono">
          <div className="flex justify-between gap-2 mb-1">
            <span>Format</span>
            <span className="text-ink-2">Best av 3 sett</span>
          </div>
          <div className="flex justify-between gap-2 mb-1">
            <span>K-faktor</span>
            <span className="text-ink-2">{match.kFactor || 64}</span>
          </div>
          <div className="flex justify-between gap-2 mb-1">
            <span>Registrert av</span>
            <span className="text-ink-2">{match.registeredBy || 'admin'}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span>Match-ID</span>
            <span className="text-ink-2 truncate ml-2" style={{ maxWidth: '60%' }} title={match.id}>
              {match.id}
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex gap-2 justify-between px-5 py-3 border-t border-line flex-wrap flex-shrink-0">
        <button type="button" onClick={onDelete} className="btn-danger text-xs">
          <Trash2 size={12} /> Slett kamp
        </button>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="btn-outline text-sm">
            Lukk
          </button>
          <button type="button" onClick={onEdit} className="btn-lime text-sm">
            <Edit2 size={12} /> Endre resultat
          </button>
        </div>
      </div>
    </Dialog>
  );
}
